import React, { useMemo } from 'react';
import { Trophy, SoccerBall, ShieldCheck, Star, Baby } from '@phosphor-icons/react';
import { getPlayerStat } from '../utils/gameData';

const scoreMVP = (p, comp) =>
  getPlayerStat(p, 'goals', comp) * 3 +
  getPlayerStat(p, 'assists', comp) * 2 +
  getPlayerStat(p, 'cleanSheets', comp) * 2 -
  getPlayerStat(p, 'yellowCards', comp) * 0.5 -
  getPlayerStat(p, 'redCards', comp) * 2;

const RaceItem = ({ rank, player, statValue, highlight = false }) => (
  <div className={`flex items-center gap-3 px-3 py-2 rounded-sm transition-all ${highlight ? 'bg-[#FFD700]/10 border border-[#FFD700]/30' : 'bg-[#0A0A0C]'}`}>
    <span className={`font-mono font-bold text-lg w-6 ${rank === 1 ? 'text-[#FFD700]' : rank === 2 ? 'text-[#C0C0C0]' : rank === 3 ? 'text-[#CD7F32]' : 'text-[#6E6E73]'}`}>{rank}</span>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-white truncate">{player.flag} {player.name}</p>
      <p className="text-[10px] text-[#6E6E73]">{player.teamName} | {player.position} | Age {player.age}</p>
    </div>
    <span className="font-mono font-bold text-lg text-white">{statValue}</span>
  </div>
);

export const AwardRace = ({ teams, title, type = 'league', competition = 'league' }) => {
  const allPlayers = useMemo(() =>
    teams.flatMap(team => team.players.map(p => ({ ...p, teamName: team.name }))), [teams]);

  const topScorers = useMemo(() =>
    [...allPlayers].filter(p => getPlayerStat(p, 'goals', competition) > 0)
      .sort((a, b) => getPlayerStat(b, 'goals', competition) - getPlayerStat(a, 'goals', competition)).slice(0, 25),
    [allPlayers, competition]);

  const bestGK = useMemo(() =>
    [...allPlayers].filter(p => p.position === 'GK' && getPlayerStat(p, 'cleanSheets', competition) > 0)
      .sort((a, b) => getPlayerStat(b, 'cleanSheets', competition) - getPlayerStat(a, 'cleanSheets', competition)).slice(0, 25),
    [allPlayers, competition]);

  const mvpRace = useMemo(() =>
    [...allPlayers].map(p => ({ ...p, mvpScore: scoreMVP(p, competition) }))
      .filter(p => p.mvpScore > 0)
      .sort((a, b) => b.mvpScore - a.mvpScore).slice(0, 25),
    [allPlayers, competition]);

  const bestYoung = useMemo(() =>
    [...allPlayers].filter(p => p.age <= 23)
      .map(p => ({ ...p, youngScore: scoreMVP(p, competition) }))
      .filter(p => p.youngScore > 0)
      .sort((a, b) => b.youngScore - a.youngScore).slice(0, 25),
    [allPlayers, competition]);

  const hasData = topScorers.length > 0 || mvpRace.length > 0;
  if (!hasData) {
    return (
      <div className="card-surface rounded-sm p-6 text-center">
        <Trophy size={32} className="text-[#6E6E73] mx-auto mb-2" />
        <p className="text-[#6E6E73] text-sm">Simulate matches to see award race standings</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={`award-race-${type}`}>
      <h3 className="font-heading font-bold text-white uppercase tracking-wide flex items-center gap-2">
        <Trophy size={18} weight="fill" className="text-[#FFD700]" />
        {title}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AwardSection title="Golden Boot Race (Top 25)" icon={SoccerBall} color="text-[#FF3B30]"
          data={topScorers} valueFn={p => getPlayerStat(p, 'goals', competition)} />
        <AwardSection title="MVP Race (Top 25)" icon={Star} color="text-[#FFD700]"
          data={mvpRace} valueFn={p => Math.round(p.mvpScore * 10) / 10} />
        <AwardSection title="Best GK Race (Top 25)" icon={ShieldCheck} color="text-[#32D74B]"
          data={bestGK} valueFn={p => getPlayerStat(p, 'cleanSheets', competition)} />
        <AwardSection title="Best Young Player (Top 25)" icon={Baby} color="text-[#007AFF]"
          data={bestYoung} valueFn={p => Math.round(p.youngScore * 10) / 10} />
      </div>
    </div>
  );
};

const AwardSection = ({ title, icon: Icon, color, data, valueFn }) => (
  <div className="card-surface rounded-sm">
    <div className="border-b border-[#2C2C35] px-3 py-2 flex items-center gap-2">
      <Icon size={14} weight="fill" className={color} />
      <span className="text-xs font-bold uppercase text-[#A0A0A5] tracking-wider">{title}</span>
    </div>
    <div className="p-2 space-y-1 max-h-[400px] overflow-auto">
      {data.length === 0 ? <p className="text-[#6E6E73] text-xs p-2">No data yet</p> :
        data.map((p, i) => <RaceItem key={p.id} rank={i + 1} player={p} statValue={valueFn(p)} highlight={i === 0} />)
      }
    </div>
  </div>
);

// Award Ceremony Modal Content (end-of-season)
export const AwardCeremony = ({ teams, type = 'league', label = 'EWFL League', competition = 'league' }) => {
  const allPlayers = teams.flatMap(team => team.players.map(p => ({ ...p, teamName: team.name })));

  const goldenBoot = [...allPlayers].sort((a, b) => getPlayerStat(b, 'goals', competition) - getPlayerStat(a, 'goals', competition))[0];
  const mvp = [...allPlayers].map(p => ({ ...p, mvpScore: scoreMVP(p, competition) })).sort((a, b) => b.mvpScore - a.mvpScore)[0];
  const bestGK = [...allPlayers].filter(p => p.position === 'GK').sort((a, b) => getPlayerStat(b, 'cleanSheets', competition) - getPlayerStat(a, 'cleanSheets', competition))[0];
  const bestYoung = [...allPlayers].filter(p => p.age <= 23).map(p => ({ ...p, youngScore: scoreMVP(p, competition) })).sort((a, b) => b.youngScore - a.youngScore)[0];

  const awards = [
    { label: 'Golden Boot', player: goldenBoot, stat: `${getPlayerStat(goldenBoot || {}, 'goals', competition)} Goals`, color: '#FF3B30', icon: SoccerBall },
    { label: 'MVP', player: mvp, stat: `Score: ${Math.round((mvp?.mvpScore || 0) * 10) / 10}`, color: '#FFD700', icon: Star },
    { label: 'Best Goalkeeper', player: bestGK, stat: `${getPlayerStat(bestGK || {}, 'cleanSheets', competition)} Clean Sheets`, color: '#32D74B', icon: ShieldCheck },
    { label: 'Best Young Player', player: bestYoung, stat: 'U23', color: '#007AFF', icon: Baby },
  ];

  return (
    <div className="space-y-4" data-testid={`award-ceremony-${type}`}>
      <div className="text-center mb-6">
        <Trophy size={48} weight="fill" className="text-[#FFD700] mx-auto mb-2" />
        <h2 className="font-heading font-black text-2xl text-white uppercase">{label} Awards</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {awards.map(({ label: awardLabel, player, stat, color, icon: Icon }) => (
          <div key={awardLabel} className="card-elevated rounded-sm p-4 text-center border-t-2" style={{ borderTopColor: color }}>
            <Icon size={28} weight="fill" style={{ color }} className="mx-auto mb-2" />
            <p className="text-xs font-bold uppercase tracking-wider" style={{ color }}>{awardLabel}</p>
            {player ? (
              <>
                <p className="text-lg font-bold text-white mt-2">{player.flag} {player.name}</p>
                <p className="text-sm text-[#A0A0A5]">{player.teamName}</p>
                <p className="font-mono text-sm mt-1" style={{ color }}>{stat}</p>
              </>
            ) : (
              <p className="text-sm text-[#6E6E73] mt-2">No winner</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
